import * as React from 'react';
import { surfaceVariantClasses } from '../../shared/styleTokens';

/**
 * Panel variant types
 * - default: Background with border and subtle shadow (shadow-sm)
 * - elevated: Stronger shadow (shadow-md), no border
 * - outlined: No shadow, 1px border only
 * - ghost: No shadow, no border, no background
 */
export type PanelVariant = 'default' | 'elevated' | 'outlined' | 'ghost';

/**
 * Panel padding sizes based on density modes
 * - none: No padding
 * - compact: 12px padding (for data-heavy screens)
 * - default: 20px padding (standard interfaces)
 * - comfortable: 28px padding (client-facing)
 */
export type PanelPadding = 'none' | 'compact' | 'default' | 'comfortable';

/**
 * Props for the Panel component
 */
export interface PanelProps extends React.HTMLAttributes<HTMLElement> {
  /**
   * Visual variant of the panel
   * @default 'default'
   */
  variant?: PanelVariant;
  /**
   * Padding size for the panel content
   * @default 'default'
   */
  padding?: PanelPadding;
  /**
   * The HTML element or React component to render as
   * @default 'div'
   */
  as?: React.ElementType;
  /**
   * Panel content
   */
  children?: React.ReactNode;
}

/**
 * Variant classes mapping (shared with Card)
 */
const variantClasses: Record<PanelVariant, string> = surfaceVariantClasses;

/**
 * Padding classes mapping based on density modes
 */
const paddingClasses: Record<PanelPadding, string> = {
  none: '',
  compact: 'p-3', // 12px
  default: 'p-5', // 20px
  comfortable: 'p-7', // 28px
};

/**
 * Panel component
 *
 * A simple content grouping component for creating visual sections.
 * Simpler than Card - no header/footer structure, just a content container.
 *
 * @example
 * // Basic panel
 * <Panel>Content here</Panel>
 *
 * @example
 * // Panel with custom element type
 * <Panel as="section">Section content</Panel>
 *
 * @example
 * // Elevated panel with comfortable padding
 * <Panel variant="elevated" padding="comfortable">
 *   Spacious content area
 * </Panel>
 *
 * @example
 * // Panel as sidebar section
 * <Panel as="aside" variant="outlined" padding="compact">
 *   Sidebar filters
 * </Panel>
 */
const Panel = React.forwardRef<HTMLElement, PanelProps>(
  (
    {
      variant = 'default',
      padding = 'default',
      as: Component = 'div',
      className = '',
      children,
      ...props
    },
    ref
  ) => {
    const combinedClasses = [
      'rounded-lg',
      variantClasses[variant],
      paddingClasses[padding],
      className,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <Component ref={ref} className={combinedClasses} {...props}>
        {children}
      </Component>
    );
  }
);

Panel.displayName = 'Panel';

export { Panel };
